// Full-table drill-ins ("the numbers view") — sortable batting and pitching
// grids ported from the old tabs, extended with the story columns. Rendered
// inside a bottom sheet; first column sticks during horizontal scroll.

import React, { useState } from 'react';
import { GS } from '../../lib/designTokens';
import { fmtStat, StatKind } from '../../lib/stat-format';
import { Sheet } from './Sheet';
import { shortName } from './ui';
import type { SeasonData } from './useSeasonStats';

export type TableKind = 'batting' | 'pitching' | null;

interface Col {
  key: string;
  label: string;
  kind: StatKind;
  asc?: boolean; // sort ascending (lower = better)
}

const BATTING_COLS: Col[] = [
  { key: 'gp', label: 'GP', kind: 'count' },
  { key: 'pa', label: 'PA', kind: 'count' },
  { key: 'ab', label: 'AB', kind: 'count' },
  { key: 'avg', label: 'AVG', kind: 'avg3' },
  { key: 'obp', label: 'OBP', kind: 'avg3' },
  { key: 'slg', label: 'SLG', kind: 'avg3' },
  { key: 'ops', label: 'OPS', kind: 'avg3' },
  { key: 'h', label: 'H', kind: 'count' },
  { key: 'xbh', label: 'XBH', kind: 'count' },
  { key: 'bb', label: 'BB', kind: 'count' },
  { key: 'so', label: 'SO', kind: 'count' },
  { key: 'rbi', label: 'RBI', kind: 'count' },
  { key: 'r', label: 'R', kind: 'count' },
  { key: 'sb', label: 'SB', kind: 'count' },
  { key: 'qabPct', label: 'QAB%', kind: 'pct' },
  { key: 'hhb', label: 'HHB', kind: 'count' },
  { key: 'baRisp', label: 'RISP', kind: 'avg3' },
  { key: 'psPa', label: 'PS/PA', kind: 'ratio1' },
];

const PITCHING_COLS: Col[] = [
  { key: 'gp', label: 'APP', kind: 'count' },
  { key: 'pitches', label: '#P', kind: 'count' },
  { key: 'ipTrue', label: 'IP', kind: 'ip' },
  { key: 'sPct', label: 'S%', kind: 'pct' },
  { key: 'fpsPct', label: 'FPS%', kind: 'pct' },
  { key: 'p_bf', label: 'P/BF', kind: 'num2', asc: true },
  { key: 'era', label: 'ERA', kind: 'num2', asc: true },
  { key: 'fip', label: 'FIP', kind: 'num2', asc: true },
  { key: 'whip', label: 'WHIP', kind: 'num2', asc: true },
  { key: 'so', label: 'K', kind: 'count' },
  { key: 'bb', label: 'BB', kind: 'count' },
  { key: 'bbInn', label: 'BB/INN', kind: 'ratio1', asc: true },
  { key: 'bbs', label: 'BBS', kind: 'count', asc: true },
  { key: 'inn123', label: '123', kind: 'count' },
];

export const StatsTableSheet = ({ kind, data, onClose }: {
  kind: TableKind;
  data: SeasonData;
  onClose: () => void;
}) => {
  const cols = kind === 'pitching' ? PITCHING_COLS : BATTING_COLS;
  const defaultSort = kind === 'pitching' ? 'ipTrue' : 'ops';
  const [sortCol, setSortCol] = useState<string>(defaultSort);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Reset sort when switching tables
  const [lastKind, setLastKind] = useState<TableKind>(kind);
  if (kind !== lastKind) {
    setLastKind(kind);
    setSortCol(kind === 'pitching' ? 'ipTrue' : 'ops');
    setSortDir('desc');
  }

  const rows = Object.values(data.stats)
    .map((s: any) => kind === 'pitching'
      ? ((s.pitching.ip > 0 || s.pitching.pitches > 0) ? { player: s.player, v: s.pitching } : null)
      : ((s.batting.pa > 0 || s.batting.ab > 0) ? { player: s.player, v: s.batting } : null))
    .filter((r): r is { player: any; v: any } => r != null);

  const colDef = cols.find(c => c.key === sortCol);
  const sorted = [...rows].sort((a, b) => {
    const missing = colDef?.asc ? Infinity : -Infinity;
    const av = a.v[sortCol] ?? missing;
    const bv = b.v[sortCol] ?? missing;
    const base = colDef?.asc ? av - bv : bv - av;
    return sortDir === 'desc' ? base : -base;
  });

  const handleSort = (key: string) => {
    if (sortCol === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(key); setSortDir('desc'); }
  };

  return (
    <Sheet
      open={kind != null}
      onClose={onClose}
      title={kind === 'pitching' ? 'Pitching · Full Table' : 'Batting · Full Table'}
      sub="Tap any column to sort · — means that stat isn't in the import"
    >
      <div className="rounded-2xl border overflow-x-auto" style={{ background: GS.panel, borderColor: GS.line }}>
        <table className="w-full text-left whitespace-nowrap" style={{ minWidth: 640 }}>
          <thead style={{ borderBottom: `1px solid ${GS.line}` }}>
            <tr>
              <th className="px-4 py-2.5 text-[9px] font-black uppercase sticky left-0" style={{ color: GS.ink3, background: GS.panel, letterSpacing: '0.08em' }}>
                Player
              </th>
              {cols.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="px-2.5 py-2.5 text-[9px] font-black uppercase text-center cursor-pointer select-none"
                  style={{ color: sortCol === col.key ? GS.greenInk : GS.ink3, letterSpacing: '0.06em' }}
                >
                  {col.label}
                  <span className="ml-0.5 text-[8px]">{sortCol === col.key ? (sortDir === 'desc' ? '▼' : '▲') : ''}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ player, v }, idx) => (
              <tr key={player.id} style={{ borderTop: idx > 0 ? `1px solid ${GS.line}` : undefined }}>
                <td className="px-4 py-2.5 sticky left-0 text-xs font-extrabold" style={{ background: GS.panel, color: GS.ink }}>
                  {shortName(player.name)} <span className="text-[9px] font-bold" style={{ color: GS.ink3 }}>#{player.number}</span>
                </td>
                {cols.map(col => {
                  const raw = v[col.key];
                  const out = fmtStat(raw, col.kind);
                  return (
                    <td key={col.key} className="px-2.5 py-2.5 text-center text-[11px] font-bold tabular-nums" style={{ color: out === '—' ? GS.ink3 : sortCol === col.key ? GS.ink : GS.ink2 }}>
                      {out}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] font-semibold leading-relaxed mt-3 px-1" style={{ color: GS.ink3 }}>
        Em-dash = not in that import. <b style={{ color: GS.greenInk }}>Re-import the season CSV</b> to backfill the newer stats.
      </p>
    </Sheet>
  );
};
